import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';

import { RootState } from 'src/store/reducers';

import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { TextField, InputAdornment } from '@mui/material';

import Card from '@components/Card';
import BoxImage from '@components/styled-UI/BoxImage';

import FindYourLook from '@images/svg-icons/findYourLook.svg';
import MyOrders from '@images/svg-icons/my-orders-big.svg';
import MySavedItems from '@images/svg-icons/my-saved-items-big.svg';
import MyEyeExams from '@images/svg-icons/my-eye-exams-big.svg';
import MyStores from '@images/svg-icons/my-stores-big.svg';

import { IMySavedItemsListPayload, IPhysicalStore } from '@interfaces/myProfileInterfaces';
import { IGlobalOrderDetails, OnlinerOrderHistoryPayload } from '@interfaces/myPurchasesInterfaces';
import { DASHBOARD_WIDGETS } from '@interfaces/dashboardInterfaces';
import {
    AppointmentPhysicalStoreInformations,
    Appointments,
} from '@interfaces/myAppointmentInterfaces';

import { getAccessToken, getUserEyeExams } from '@services/myAppointmentServices';
import { getFavouritesStores, getMySavedItems, getPersonalDetails } from '@services/myProfileServices';
import { getEncryptedOnlineOrderId, getOnlineOrdersHistory, getRetailOrderHistory } from '@services/myPurchasesServices';
import {
    getDashboardInformations,
    getMyAccountDashboardEspot,
    getMyAccountReorderTopDashboardEspot,
    getMyAccountTopDashboardEspot,
    getMyAccountBottomDashboardEspot,
} from '@services/dashboardServices';
import { getAppointmentsList } from './in-store-services/InStoreServicesDetails';
import { getPhysicalStoreInformations } from './in-store-services/InStoreServicesAppointmentOverview';

import * as actions from '@redux-actions/';

import { getFullAddress, getSortedArray, WEEKDAYS } from '@utils/pages-utils/MyProfileUtils';
import { mapGlobalOrderDetails, sortOnlineOrdersHistory, sortRetailOrdersHistory } from '@utils/pages-utils/MyPurchasesUtils';
import { getLabelFallback } from '@utils/utils';
import { getStringFromDate } from '@utils/dateUtils';
import { fixEncoding } from "@utils/utils";

import {
    mapInStoreServicesAppointments,
    mapPhysicalStoreInformations,
    EyeExamsConfig,
} from '@utils/pages-utils/EyeExamsUtils';

import { compareAsc } from 'date-fns/esm';
import { cloneDeep } from 'lodash';
import LoaderElement from '@loaders/LoaderElement';
import { getCookie } from '@utils/cookie';
import URLS from '@utils/urlList';
import axios, { AxiosRequestConfig } from 'axios';

interface Section {
    label:
    | 'MANAGE_EYE_EXAMS'
    | 'MY_STORE'
    | 'FIND_YOUR_LOOK'
    | 'MY_FAVORITES'
    | 'MY_ORDERS'
    | 'MY_INSTORE_APPOINTMENT';
}

interface SectionElement {
    label: 'TITLE' | 'CTA_LABEL' | 'LABEL' | 'TEXT';
}

const CardContainer = (): ReactElement => {
    const [searchParam, setSearchParam] = useState<string>('');
    const navigate = useNavigate();

    const dispatch = useDispatch();

    const config = useSelector((state: RootState) => state.common.config);
    const isMobile = useSelector((state: RootState) => state.common.isMobile);

    const userEyeExams = useSelector((state: RootState) => state.myAppointment.userEyeExams);
    const favouritesStores = useSelector((state: RootState) => state.myProfile.favouritesStores);
    const mySavedItems = useSelector((state: RootState) => state.myProfile.mySavedItems);
    const onlineOrdersHistory = useSelector((state: RootState) => state.myPurchases.onlineOrders);
    const retailOrdersHistory = useSelector((state: RootState) => state.myPurchases.retailOrders);
    const inStoreAppointmentEspot = useSelector((state: RootState) => state.dashboard.espot);
    const dahsboardEspotReorderTop = useSelector((state: RootState) => state.dashboard.espotReorderTop);
    const dahsboardEspotTop = useSelector((state: RootState) => state.dashboard.espotTop);
    const dahsboardEspotBottom = useSelector((state: RootState) => state.dashboard.espotBottom);
    const dashboardInformations = useSelector((state: RootState) => state.dashboard.dashboardInformations);
    const getDashboardInformationsRequestStatus = useSelector((state: RootState) => state.dashboard.getDashboardInformationsRequestStatus);
    const encodedOrderId = useSelector((state: RootState) => state.myPurchases.encodedOrderId);
    const accessToken = useSelector((state: RootState) => state.myAppointment.accessToken);
    const personalDetails = useSelector((state: RootState) => state.myProfile.personalDetails);

    const [lastContactLensesOrderId, setLastContactLensesOrderId] = useState(0);

    const clonedEyeExamsConfig = cloneDeep(EyeExamsConfig);
    const [eyeExamsConfig, setEyeExamsConfig] = useState(clonedEyeExamsConfig);

    const [inStoreAppointment, setInStoreAppointment] = useState<{
        appointmentsList: Appointments[];
        physicalStoreInformations: AppointmentPhysicalStoreInformations;
    }>({ appointmentsList: null, physicalStoreInformations: null });

    const [orderItemsAnalytics, setOrderItemsAnalytics] = useState(null);

    useEffect(() => {
        dispatch(getPersonalDetails(config.mockRequest, config.storeId));
    }, []);

    const eMail = useMemo(() => {
        const email = getCookie('email_address');
        return personalDetails?.email || email;
    }, [personalDetails?.email]);

    useEffect(() => {
        const mySavedItemsPayload: IMySavedItemsListPayload = {
            isMockRequest: config.mockRequest,
            storeId: config.storeId,
            catalogId: config.catalogId,
            langId: config.langId,
        };

        const onlineOrdersPayload: OnlinerOrderHistoryPayload = {
            isMockRequest: config.mockRequest,
            storeId: config.storeId,
            currency: config.currencyCode,
            pageNumber: 1,
            pageSize: 50,
        };

        dispatch(getUserEyeExams(config.mockRequest, config.storeId, config.endPointStatusExams, config.difKeyStatusAppointments, config.authEncoded));
        dispatch(getFavouritesStores(config.mockRequest, config.storeId));
        dispatch(getMySavedItems(mySavedItemsPayload));
        dispatch(getOnlineOrdersHistory(onlineOrdersPayload));
        dispatch(getMyAccountDashboardEspot(config.mockRequest, config.storeId));
        dispatch(getMyAccountReorderTopDashboardEspot(config.mockRequest, config.storeId));
        dispatch(getMyAccountTopDashboardEspot(config.mockRequest, config.storeId));
        dispatch(getMyAccountBottomDashboardEspot(config.mockRequest, config.storeId));
        dispatch(getDashboardInformations(config.mockRequest, config.storeId));
        dispatch(getRetailOrderHistory(config.mockRequest, config.storeId, eMail));

        return () => {
            actions.getUserEyeExamsSuccess(null),
                actions.getFavouritesStoresSuccess([]),
                actions.getMySavedItemsSuccess(null),
                actions.getOnlineOrdersHistorySuccess([]),
                actions.getMyAccountDashboardEspotSuccess(null),
                actions.getMyAccountReorderTopDashboardEspotSuccess(null),
                actions.getMyAccountTopDashboardEspotSuccess(null),
                actions.getDashboardInformationsSuccess(null);
            actions.getRetailOrderHistorySuccess([]);
        };
    }, []);

    /*useEffect(() => {
        encodedOrderId && goToProvidePrescriptionSection(encodedOrderId);
        return () => {
            dispatch(actions.getEncryptedOnlineOrderIdSuccess(null));
        };
    }, [encodedOrderId]);*/

    useEffect(() => {
        const url = '/wcs/resources/ocr/{storeId}/lastContactLensesOrder/{userId}'
            .replace('{storeId}', window['ocr'].storeId)
            .replace('{userId}', window['ocr'].userId);

        fetch(url)
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response was not ok ' + response.statusText);
                }
                return response.json();
            })
            .then(json => {
                setLastContactLensesOrderId(json.response.data.dataContent.lastOrderId);
            })
            .catch(error => {
                console.error('There was a problem with the fetch operation:', error);
            });
    }, []);

    const mapOrderItemsAnalytics = (orderItems: IGlobalOrderDetails) => {
        let objects = {};
        if (orderItems.hasContactLenses && orderItems.orderStatus === "D") {
            orderItems.orderItems.forEach(item => {
                if (Array.isArray(item.partNumber)) {
                    item.partNumber.forEach((id, index) => {
                        objects[id] = {
                            "Status": item.orderItemInventoryStatus,
                            "Category": "CONTACT LENSES",
                            "LensType": "PLANO",
                            "Price": item.newPrice.replace("$ ", ""),
                            "PriceFull": item.oldPrice.replace("$ ", ""),
                            "Units": Number(item.quantity[index]).toString(),
                            "ModelName": item.frame,
                            "Brand": item.brand,
                            "ReorderSource": "Ecom",
                            "Image": item.imgUrl,
                            "Url": window.location.origin + item.productPageUrl
                        }
                    })
                } else {
                    objects[item.partNumber] = {
                        "Status": item.orderItemInventoryStatus,
                        "Category": "CONTACT LENSES",
                        "LensType": "PLANO",
                        "Price": item.newPrice.replace("$ ", ""),
                        "PriceFull": item.oldPrice.replace("$ ", ""),
                        "Units": Number(item.quantity).toString(),
                        "ModelName": item.frame,
                        "Brand": item.brand,
                        "ReorderSource": "Ecom",
                        "Image": item.imgUrl,
                        "Url": window.location.origin + item.productPageUrl
                    }
                }
            })

            setOrderItemsAnalytics(objects)
        }
    }

    useEffect(() => {
        if (!!lastContactLensesOrderId && lastContactLensesOrderId !== 0) {
            const globalOrderDetailsUrl = URLS.GET_GLOBAL_ORDER_DETAILS.replace(
                '{storeId}',
                config.storeId.toString()
            )
                .replace('{orderId}', lastContactLensesOrderId.toString())
                .replace('{currency}', config.currencyCode)
                .replace('{pageNumber}', '1')
                .replace('{pageSize}', '50')
                .replace('{noCache}', new Date().getTime().toString());

            const req: AxiosRequestConfig = {
                method: 'GET',
                url: globalOrderDetailsUrl,
                headers: { 'Content-Type': 'application/json' },
            };

            axios(req)
                .then(result => {
                    const globalOrderDetails = mapGlobalOrderDetails(result?.data)
                    mapOrderItemsAnalytics(globalOrderDetails)
                })
                .catch(() => {
                    console.error('Error in get order detail');
                });
        }
    }, [lastContactLensesOrderId])

    useEffect(() => {
        if (accessToken) {
            getAppointmentsList(
                {
                    isMockRequest: config.mockRequest,
                    subscriptionKey: config.subscriptionKey,
                    accessToken: accessToken,
                },
                config
            )
                .then(result => {
                    const mappedAppointmentsLists = mapInStoreServicesAppointments(result?.data);
                    setInStoreAppointment({
                        ...inStoreAppointment,
                        appointmentsList: mappedAppointmentsLists,
                    });
                })
                .catch(() => setInStoreAppointment(null));
        }
    }, [accessToken]);

    useEffect(() => {
        dispatch(
            getAccessToken({
                isMockRequest: config.mockRequest,
                brand: 'LC',
                userEmail: config.userEmail,
                userId: config.userId,
                storeId: config.storeId,
                role: 'CUSTOMER',
            })
        );
        return () => {
            dispatch(actions.getAccessTokenSuccess(null));
        };
    }, []);

    useEffect(() => {
        inStoreAppointment?.appointmentsList &&
            !inStoreAppointment?.physicalStoreInformations &&
            getPhysicalStoreInformations({
                isMockRequest: config.mockRequest,
                storeId: config.storeId,
                physicalStoreId: inStoreAppointment.appointmentsList[0].storeNumber,
            }).then(result => {
                const mappedPhysicalStoreInformations = mapPhysicalStoreInformations(
                    result?.data?.PhysicalStore[0]
                );
                setInStoreAppointment({
                    ...inStoreAppointment,
                    physicalStoreInformations: mappedPhysicalStoreInformations,
                });
            });
    }, [inStoreAppointment?.appointmentsList]);

    useEffect(() => {
        !!userEyeExams &&
            userEyeExams.length &&
            userEyeExams.map(exam => {
                compareAsc(new Date(exam.timestamp), new Date()) === 1
                    ? clonedEyeExamsConfig.upcoming.eyeExams.push(exam)
                    : clonedEyeExamsConfig.past.eyeExams.push(exam);

                setEyeExamsConfig(clonedEyeExamsConfig);
            });
    }, [userEyeExams]);

    const handleSearchParamChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchParam(event.target.value);
    };

    const handleStoreSearching = (event: React.KeyboardEvent<HTMLDivElement>) => {
        event.persist();
        const { key } = event;
        key === 'Enter' && getCardCtaOnClick(myStores, { label: 'MY_STORE' });
    };

    /** The function below opens Google Maps with the store address prefilled */

    const goToStoreLocation = (physicalStore: IPhysicalStore) => {
        window.open(`http://maps.google.com/?q=${getFullAddress(physicalStore)}`, '_blank');
    };

    /** The function below redirects the user to the section for providing the RX prescription */
    const goToProvidePrescriptionSection = (orderId: string, orderAddressEmail: string) => {
        window.open(
            config.callFromComponentToTO.myPurchases.onlineOrders.sendItNow
                .replace('{catalogId}', config.catalogId)
                .replace('{langId}', config.langId)
                .replace('{storeId}', config.storeId)
                .replace('{orderId}', orderId)
                .replace('{email}', encodeURIComponent(orderAddressEmail)),
            '_parent'
        );
    };

    /** The function below redirects to the store page */

    const goToFindStore = (e: React.MouseEvent<HTMLDivElement>) => {
        const element = (e.target as Element).closest('button');
        if (element && e.currentTarget.contains(element)) {
            window.open(config.callFromComponentToTO.dashboard.bookAnAppointment, '_parent');
        }
    };

    /** Get the phyisical store hours formatted with the week days */

    const getStoreHours = (storeHours: string): JSX.Element[] => {
        const hours: JSX.Element[] = [];
        storeHours.split(';').map((storeHour, index) => {
            hours.push(
                <div
                    role="region"
                    aria-label="My stores hours"
                    className={`${WEEKDAYS[index]}`}
                    key={index}
                >
                    <span
                        role="region"
                        aria-label="My stores hour label"
                        className="secondary-message small bold align-left"
                    >
                        {getLabelFallback(
                            `DASHBOARD_MY_STORE_CARD_STORE_HOURS_${WEEKDAYS[
                                index
                            ].toUpperCase()}_LABEL`
                        )}
                    </span>
                    <span
                        role="region"
                        aria-label="My stores hour value"
                        className="secondary-message small"
                    >
                        {storeHour}
                    </span>
                </div>
            );
        });
        return hours;
    };

    const getCardCtaOnClick = (obj, section: Section) => {
        switch (section.label) {
            case 'MANAGE_EYE_EXAMS':
                return navigate(config.pagesUrl.myAppointments.eyeExams);
            case 'MY_STORE':
                return obj && obj?.length > 0
                    ? navigate(config.pagesUrl.myProfile.myStores)
                    : window.open(
                        config.callFromComponentToTO.dashboard.myStore
                            .replace('{LOCATION_ENTRY}', searchParam ? searchParam : 'new+york')
                            .replace('{CATALOG_ID}', config.catalogId)
                            .replace('{LANG_ID}', config.langId)
                            .replace('{STORE_ID}', config.storeId),
                        '_parent'
                    );
            case 'FIND_YOUR_LOOK':
                return window.open(config.callFromComponentToTO.dashboard.findYourLook, '_parent');
            case 'MY_FAVORITES':
                return obj
                    ? window.open(config.pagesUrl.myProfile.mySavedItems, '_parent')
                    : window.open(
                        config.callFromComponentToTO.dashboard.mySavedItems
                            .replace('{CATALOG_ID}', config.catalogId)
                            .replace('{LANG_ID}', config.langId)
                            .replace('{STORE_ID}', config.storeId),
                        '_parent'
                    );
            case 'MY_ORDERS':
                return obj && obj.length > 0
                    ? navigate(config.pagesUrl.myPurchases.onlineOrders)
                    : window.open(config.callFromComponentToTO.dashboard.myOrders, '_parent');
            case 'MY_INSTORE_APPOINTMENT':
                return navigate(config.pagesUrl.myAppointments.inStoreServices);
        }
    };

    const getText = (obj, section: Section, element: SectionElement): string => {
        if (!obj || (Array.isArray(obj) && !obj?.length)) {
            return getLabelFallback('DASHBOARD_' + section.label + '_EMPTY_CARD_' + element.label);
        }

        return getLabelFallback('DASHBOARD_' + section.label + '_CARD_' + element.label);
    };

    const getCardSubTitle = (
        obj: any,
        section: Section,
        element: SectionElement
    ): string | null => {
        if (
            (section.label === 'MY_FAVORITES' && (obj?.totalCount === 0 || !obj?.totalCount)) ||
            (section.label !== 'MY_FAVORITES' && !obj?.length)
        ) {
            return null;
        }

        if (section.label === 'MY_FAVORITES' && obj?.totalCount === 1) {
            return getLabelFallback('DASHBOARD_MY_FAVORITES_CARD_SUBTITLE_SINGULAR_LABEL');
        }

        if (section.label === 'MY_ORDERS' && obj.length === 1) {
            return getLabelFallback('DASHBOARD_MY_ORDERS_CARD_SUBTITLE_SINGULAR_LABEL');
        }

        if (section.label === 'MY_STORE' && obj.length === 1) {
            return getLabelFallback('DASHBOARD_MY_STORE_CARD_SUBTITLE_SINGULAR_LABEL');
        }

        if (section.label === 'MANAGE_EYE_EXAMS') {
            return getLabelFallback(
                `DASHBOARD_MANAGE_EYE_EXAMS_CARD_SUBTITLE_${obj?.length > 1 ? 'PLURAL' : 'SINGULAR'
                }_LABEL`
            ).replace('{TOTAL}', obj?.length);
        }

        return getLabelFallback(
            `DASHBOARD_${section.label}_CARD_SUBTITLE_PLURAL_${element.label}`
        ).replace('{TOTAL}', section.label === 'MY_FAVORITES' ? obj?.totalCount : obj.length);
    };

    const getCardContent = (obj, section: Section, element: SectionElement): ReactElement => {
        if (!obj) {
            if (section.label !== 'MY_STORE') {
                return (
                    <div
                        role="region"
                        aria-label="card text"
                        className={`card-text ${section.label === 'MANAGE_EYE_EXAMS'
                            ? 'card-text-mod'
                            : section.label === 'MY_ORDERS'
                                ? 'card-text-simple-orders'
                                : 'card-text-simple'
                            }`}
                    >
                        {getLabelFallback(
                            'DASHBOARD_' + section.label + '_EMPTY_CARD_' + element.label
                        )}
                    </div>
                );
            } else {
                return (
                    <>
                        <div
                            role="region"
                            aria-label="card text"
                            className="card-text card-text-input"
                        >
                            {getLabelFallback(
                                'DASHBOARD_' + section.label + '_EMPTY_CARD_' + element.label
                            )}
                        </div>
                        <div
                            role="region"
                            aria-label="search bar"
                            className="card-input form-group search-store"
                        >
                            <TextField
                                name="searchParam"
                                variant="outlined"
                                placeholder={getLabelFallback(
                                    'DASHBOARD_MY_STORE_INPUT_PLACEHOLDER'
                                )}
                                value={searchParam}
                                onChange={handleSearchParamChange}
                                onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) =>
                                    handleStoreSearching(e)
                                }
                                className="input-search"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment className="extra-large" position="end">
                                            <button
                                                className="primary-button black button-size-small search-button"
                                                data-element-id={
                                                    'MyHub_' +
                                                    getText(myStores, { label: 'MY_STORE' }, { label: 'CTA_LABEL' })
                                                }
                                                onClick={() => getCardCtaOnClick(myStores, { label: 'MY_STORE' })}
                                            >
                                                {getText(myStores, { label: 'MY_STORE' }, { label: 'CTA_LABEL' })}
                                            </button>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                        </div>
                    </>
                );
            }
        } else {
            switch (section.label) {
                case 'MANAGE_EYE_EXAMS':
                    return (
                        <div
                            role="region"
                            aria-label="eye exams card title"
                            className="card-table"
                        //className="card-grid card-grid-5 mb-24"
                        >
                            <div className="card-table-row first">
                                <div
                                    role="region"
                                    aria-label="eye exams card patient field"
                                    className="card-table-row-title"
                                //className="card-grid-r1c1 card-grid-title"
                                >
                                    {getLabelFallback('DASHBOARD_MANAGE_EYE_EXAMS_CARD_PATIENT_LABEL')}
                                </div>
                                <div
                                    role="region"
                                    aria-label="eye exams card patient value"
                                    className="card-table-row-text"
                                //className="card-grid-r1c2 card-grid-text"
                                >
                                    {`${obj?.patient?.firstName} ${obj?.patient?.lastName}`}
                                </div>
                            </div>
                            <div className="card-table-row">
                                <div
                                    role="region"
                                    aria-label="eye exams card date field"
                                    className="card-table-row-title"
                                //className="card-grid-r1c1 card-grid-title"
                                >
                                    {getLabelFallback('DASHBOARD_MANAGE_EYE_EXAMS_CARD_DATE_LABEL')}
                                </div>
                                {obj?.date && (
                                    <div
                                        role="region"
                                        aria-label="eye exams card date value"
                                        className="card-table-row-text"
                                    //className="card-grid-r1c2 card-grid-text"
                                    >
                                        {obj.date}
                                    </div>
                                )}
                            </div>
                            <div className="card-table-row">
                                <div
                                    role="region"
                                    aria-label="eye exams card time field"
                                    className="card-table-row-title"
                                //className="card-grid-r1c1 card-grid-title"
                                >
                                    {getLabelFallback('DASHBOARD_MANAGE_EYE_EXAMS_CARD_TIME_LABEL')}
                                </div>
                                {obj?.time && (
                                    <div
                                        role="region"
                                        aria-label="eye exams card time value"
                                        className="card-table-row-text"
                                    //className="card-grid-r1c2 card-grid-text"
                                    >
                                        {obj.time}
                                    </div>
                                )}
                            </div>
                            <div className="card-table-row">
                                <div
                                    role="region"
                                    aria-label="eye exams card doctor field"
                                    className="card-table-row-title"
                                //className="card-grid-r1c1 card-grid-title"
                                >
                                    {getLabelFallback('DASHBOARD_MANAGE_EYE_EXAMS_CARD_DOCTOR_LABEL')}
                                </div>
                                {obj?.provider && (
                                    <div
                                        role="region"
                                        aria-label="eye exams card doctor value"
                                        className="card-table-row-text"
                                    //className="card-grid-r1c2 card-grid-text"
                                    >
                                        {obj.provider}
                                    </div>
                                )}
                            </div>
                            <div className="card-table-row">
                                <div
                                    role="region"
                                    aria-label="eye exams card location field"
                                    className="card-table-row-title"
                                //className="card-grid-r1c1 card-grid-title"
                                >
                                    {getLabelFallback('DASHBOARD_MANAGE_EYE_EXAMS_CARD_LOCATION_LABEL')}
                                </div>
                                {obj?.office && (
                                    <div
                                        role="region"
                                        aria-label="eye exams card location value"
                                        className="card-table-row-text multiple"
                                    //className="card-grid-r5c2 card-grid-text card-grid-text-multiple"
                                    >
                                        {obj.office?.address}
                                        <span>{obj.office?.city} </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                case 'MY_STORE':
                    return (
                        <div
                            role="region"
                            aria-label="My stores informations"
                            className="card-store"
                        >
                            {obj?.storeName && (
                                <div
                                    role="region"
                                    aria-label="My stores store name"
                                    className="card-store-name"
                                >
                                    {obj.storeName.toLowerCase()}
                                </div>
                            )}
                            <div
                                role="region"
                                aria-label="My stores store address"
                                className="card-store-address"
                            >
                                <span>{obj?.addressLine?.toLowerCase()},</span>
                                <span>{` ${obj?.city?.toLowerCase()}, ${obj?.stateOrProvinceName}, ${obj?.postalCode}`}</span>
                            </div>
                            <div
                                role="region"
                                aria-label="My stores store info"
                                className="card-store-info"
                            >
                                {obj?.telephone1 &&
                                    <a href={`tel:${obj.telephone1}`} className='telephone'>{obj.telephone1}</a>}
                                <button
                                    className="get-directions-label"
                                    onClick={() => goToStoreLocation(obj)}
                                >
                                    {getLabelFallback('DASHBOARD_MY_STORE_CARD_DIRECTIONS_LABEL')}
                                </button>
                            </div>
                            <div className="card-store-hours">
                                <div className="card-store-hours-title">
                                    {getLabelFallback('DASHBOARD_MY_STORE_CARD_STORE_HOURS_LABEL')}
                                </div>
                                {obj?.storeHours && (
                                    <div className="card-store-hours-grid">
                                        {getStoreHours(obj.storeHours)}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                case 'MY_FAVORITES':
                    return (
                        <div
                            role="region"
                            aria-label="my favorites informations"
                            className="card-favorites"
                        >
                            {obj?.savedItem?.imgUrl && (
                                <div
                                    role="region"
                                    aria-label="my favorites img field"
                                    className="card-favorites-image"
                                >
                                    <BoxImage
                                        imageUrl={obj?.savedItem?.imgUrl}
                                        fallbackLabel={getLabelFallback(
                                            'IMAGE_NON_AVAILABLE_LABEL'
                                        )}
                                        imageAlt="Product image"
                                    />
                                </div>
                            )}
                            {obj?.savedItem?.brandName && (
                                <div
                                    role="region"
                                    aria-label="my favorites brand value"
                                    className="card-favorites-brand"
                                >
                                    {obj?.savedItem?.brandName}
                                </div>
                            )}
                            <div className='card-favorites-bottom'>
                                {obj?.savedItem?.modelName && (
                                    <div
                                        role="region"
                                        aria-label="my favorites model name name"
                                        className="card-favorites-name"
                                    >
                                        {fixEncoding(obj?.savedItem?.modelName)}
                                    </div>
                                )}
                                <div
                                    role="region"
                                    aria-label="my favorites prices"
                                    className="card-favorites-prices"
                                >
                                    {obj?.totalPercentageOff && (
                                        <span
                                            role="region"
                                            aria-label="my favorites discounted percentage value"
                                            className="extra-small percentage bold align-left"
                                        >
                                            {getLabelFallback(
                                                'DASHBOARD_MY_FAVORITES_CARD_OFF_LABEL'
                                            ).replace('{DISCOUNT_PERCENTAGE}', obj.totalPercentageOff)}
                                        </span>
                                    )}
                                    {obj?.totalPrice !== obj?.totalDiscountedPrice && (
                                        <span
                                            role="region"
                                            aria-label="my favorites price value"
                                            className="strikethrough-price secondary-message small dark-grey align-left"
                                        >
                                            {obj?.totalPrice}
                                        </span>
                                    )}
                                    {obj?.totalDiscountedPrice && (
                                        <span
                                            role="region"
                                            aria-label="my favorites discounted price value"
                                            className="price-discounted medium align-left"
                                        >
                                            {obj?.totalDiscountedPrice}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                case 'MY_ORDERS':
                    return (
                        <>
                            {obj?.orderId && (
                                <div
                                    role="region"
                                    aria-label="my orders orderid field"
                                    className="card-order-number"
                                >
                                    <div className='order-name'>
                                        {getLabelFallback('DASHBOARD_MY_ORDERS_CARD_ORDER_LABEL')}
                                    </div>
                                    <div className='order-id'>
                                        {getLabelFallback('DASHBOARD_MY_ORDERS_CARD_ORDER_ID_LABEL').replace('{ORDER_NUMBER}', obj.orderId)}
                                    </div>
                                </div>
                            )}
                            <div
                                role="region"
                                aria-label="my orders informations"
                                className="card-table"
                            //className="card-grid card-grid-3 mb-64"
                            >
                                {obj?.placedDate && (
                                    <div className="card-table-row">
                                        <div
                                            role="region"
                                            aria-label="my orders date field"
                                            className="card-table-row-title"
                                        //className="card-grid-r1-c1 card-grid-title"
                                        >
                                            {getLabelFallback(
                                                'DASHBOARD_MY_ORDERS_CARD_DATE_LABEL'
                                            )}
                                        </div>
                                        <div
                                            role="region"
                                            aria-label="my orders date value"
                                            className="card-table-row-text"
                                        //className="card-grid-r1-c2 card-grid-text"
                                        >
                                            {getStringFromDate(
                                                new Date(obj.placedDate),
                                                'MM/dd/yyyy'
                                            )}
                                        </div>
                                    </div>
                                )}

                                {obj?.orderStatus && (
                                    <div className="card-table-row">
                                        <div
                                            role="region"
                                            aria-label="my orders order status field"
                                            className="card-table-row-title"
                                        //className="card-grid-r1-c1 card-grid-title"
                                        >
                                            {getLabelFallback(
                                                'DASHBOARD_MY_ORDERS_CARD_STATUS_LABEL'
                                            )}
                                        </div>
                                        <div
                                            role="region"
                                            aria-label="my orders order status value"
                                            className="card-table-row-text card-order-status"
                                        //className="card-grid-r2-c2 card-grid-text card-order-status"
                                        >
                                            {obj.orderStatus === 'D'
                                                ? getLabelFallback(
                                                    'DASHBOARD_MY_ORDERS_CARD_STATUS_COMPLETED_LABEL'
                                                )
                                                : obj.orderStatus === 'M' &&
                                                    !obj?.isPrescriptionRequired
                                                    ? getLabelFallback(
                                                        'DASHBOARD_MY_ORDERS_CARD_STATUS_PROGRESS_LABEL'
                                                    )
                                                    : obj.orderStatus === 'M' &&
                                                        obj?.isPrescriptionRequired
                                                        ? getLabelFallback(
                                                            'DASHBOARD_MY_ORDERS_CARD_STATUS_PRESCRIPTION_REQUIRED_LABEL'
                                                        )
                                                        : obj.orderStatus === 'X'
                                                            ? getLabelFallback(
                                                                'DASHBOARD_MY_ORDERS_CARD_STATUS_CANCELLED_LABEL'
                                                            ) : ''}
                                            {obj.orderStatus === 'M' &&
                                                obj?.isPrescriptionRequired && (
                                                    <a
                                                        className="primary-message medium bold"
                                                        onClick={() => {
                                                            /*dispatch(
                                                                    getEncryptedOnlineOrderId({
                                                                        isMockRequest:
                                                                            config.mockRequest,
                                                                        storeId: config.storeId,
                                                                        orderId: Number(obj.orderId),
                                                                    })
                                                                )*/
                                                            goToProvidePrescriptionSection(
                                                                obj?.orderId,
                                                                obj?.orderAddressEmail
                                                            );
                                                        }}
                                                    >
                                                        {getLabelFallback(
                                                            'DASHBOARD_MY_ORDERS_CARD_STATUS_PROGRESS_LINK_LABEL'
                                                        )}
                                                    </a>
                                                )}
                                            {!!obj.isRetail && (
                                                getLabelFallback(
                                                    obj.orderStatus.toLowerCase().includes('order receive')
                                                        ? 'MYPURCHASES_RETAIL_ORDERS_ORDER_STATUS_ORDER_RECEIVE'
                                                        : obj.orderStatus.toLowerCase() === 'in process'
                                                            ? 'MYPURCHASES_RETAIL_ORDERS_ORDER_STATUS_IN_PROCESS'
                                                            : obj.orderStatus.toLowerCase() === 'shipped'
                                                                ? 'MYPURCHASES_RETAIL_ORDERS_ORDER_STATUS_SHIPPED'
                                                                : obj.orderStatus.toLowerCase() === 'ready for pickup'
                                                                    ? 'MYPURCHASES_RETAIL_ORDERS_ORDER_STATUS_READY_FOR_PICKUP'
                                                                    : obj.orderStatus.toLowerCase() === 'dispensed'
                                                                        ? 'MYPURCHASES_RETAIL_ORDERS_ORDER_STATUS_DISPENSED'
                                                                        : obj.orderStatus.toLowerCase() === 'please call the store'
                                                                            ? 'MYPURCHASES_RETAIL_ORDERS_ORDER_STATUS_PLEASE_CALL_THE_STORE'
                                                                            : obj.orderStatus.toLowerCase() === 'delivered'
                                                                                ? 'MYPURCHASES_RETAIL_ORDERS_ORDER_STATUS_DELIVERED'
                                                                                : obj.orderStatus.toLowerCase() === 'returned'
                                                                                    ? 'MYPURCHASES_RETAIL_ORDERS_ORDER_STATUS_RETURNED'
                                                                                    : obj.orderStatus.toLowerCase() === 'in progress'
                                                                                        ? 'MYPURCHASES_RETAIL_ORDERS_ORDER_STATUS_IN_PROCESS'
                                                                                        : ''
                                                ).replace("${storeNumber}", obj.retailOrderDetails.store.phone)
                                            )}
                                        </div>
                                    </div>
                                )}

                                {(obj?.grandTotalCurrency || obj?.grandTotal) && (
                                    <div className="card-table-row last">
                                        <div
                                            role="region"
                                            aria-label="my orders order total field"
                                            className="card-table-row-title"
                                        //className="card-grid-r1-c1 card-grid-title"
                                        >
                                            {getLabelFallback(
                                                'DASHBOARD_MY_ORDERS_CARD_TOTAL_LABEL'
                                            )}
                                        </div>
                                        <div
                                            role="region"
                                            aria-label="my orders order total value"
                                            className="card-table-row-text"
                                        //className="card-grid-r3-c2 card-grid-text"
                                        >
                                            {`${obj?.grandTotalCurrency
                                                ? ['USD', 'CAD'].includes(
                                                    obj.grandTotalCurrency.toUpperCase()
                                                )
                                                    ? '$'
                                                    : obj.grandTotalCurrency
                                                : ''
                                                } ${parseFloat(obj?.grandTotal)}`}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    );
                case 'MY_INSTORE_APPOINTMENT':
                    return (
                        <div
                            role="region"
                            aria-label="my in-store appointment informations"
                            className="card-grid card-grid-3 mb-60"
                        >
                            {obj?.appointmentsList?.[0]?.date && (
                                <>
                                    <div
                                        role="region"
                                        aria-label="my in-store appointment date field"
                                        className="card-grid-r1c1 card-grid-title"
                                    >
                                        {getLabelFallback(
                                            'DASHBOARD_MY_INSTORE_APPOINTMENT_CARD_DATE_LABEL'
                                        )}
                                    </div>
                                    <div
                                        role="region"
                                        aria-label="my in-store appointment date value"
                                        className="card-grid-r1c2 card-grid-text"
                                    >
                                        {getStringFromDate(
                                            new Date(obj.appointmentsList[0].date),
                                            'EEEE, MMM dd yyyy'
                                        )}
                                    </div>
                                    <div
                                        role="region"
                                        aria-label="my in-store appointment time field"
                                        className="card-grid-r2c1 card-grid-title"
                                    >
                                        {getLabelFallback(
                                            'DASHBOARD_MY_INSTORE_APPOINTMENT_CARD_TIME_LABEL'
                                        )}
                                    </div>
                                    <div
                                        role="region"
                                        aria-label="my in-store appointment time value"
                                        className="card-grid-r2c2 card-grid-text"
                                    >
                                        {getStringFromDate(
                                            new Date(obj.appointmentsList[0].date),
                                            'HH:mm a'
                                        )}
                                    </div>
                                </>
                            )}

                            {obj?.appointmentsList?.[0]?.visitPurpose && (
                                <>
                                    <div
                                        role="region"
                                        aria-label="my in-store appointment visit purpose field"
                                        className="card-grid-r3c1 card-grid-title"
                                    >
                                        {getLabelFallback(
                                            'DASHBOARD_MY_INSTORE_APPOINTMENT_CARD_PURPOSE_LABEL'
                                        )}
                                    </div>
                                    <div
                                        role="region"
                                        aria-label="my in-store appointment visit purpose value"
                                        className="card-grid-r3c2 card-grid-text"
                                    >
                                        {obj.appointmentsList[0].visitPurpose}
                                    </div>
                                </>
                            )}
                            {(obj?.physicalStoreInformations?.location.officeAddress ||
                                obj?.physicalStoreInformations?.location.city ||
                                obj?.physicalStoreInformations?.location.postalCode ||
                                obj?.physicalStoreInformations?.location.province) && (
                                    <>
                                        <div
                                            role="region"
                                            aria-label="my in-store appointment location field"
                                            className="card-grid-r4c1 card-grid-title"
                                        >
                                            {getLabelFallback(
                                                'DASHBOARD_MY_INSTORE_APPOINTMENT_CARD_LOCATION_LABEL'
                                            )}
                                        </div>
                                        <div
                                            role="region"
                                            aria-label="my in-store appointment location value"
                                            className="card-grid-r4c2 card-grid-text card-grid-text-multiple"
                                        >
                                            {obj.physicalStoreInformations.location.officeAddress}

                                            <span>{obj.physicalStoreInformations.location.city}</span>

                                            <span>
                                                {`${obj.physicalStoreInformations.location.postalCode} ${obj.physicalStoreInformations.location.province}`}
                                            </span>
                                        </div>
                                    </>
                                )}
                        </div>
                    );
            }
        }

        return <></>;
    };

    const findYourLook = null;
    const myStores = favouritesStores ? getSortedArray(favouritesStores) : null;
    const myOrders = onlineOrdersHistory.length > 0 ? sortOnlineOrdersHistory(onlineOrdersHistory) : retailOrdersHistory.length > 0 ? sortRetailOrdersHistory(retailOrdersHistory) : null;

    const getCard = (cardType: string): JSX.Element => {
        switch (cardType) {
            case 'EXAMS':

                return (
                    <Card
                        key={cardType}
                        icon={!eyeExamsConfig?.upcoming?.eyeExams?.length ? <MyEyeExams /> : null}
                        title={getText(
                            userEyeExams,
                            { label: 'MANAGE_EYE_EXAMS' },
                            { label: 'TITLE' }
                        )}
                        subtitle={getCardSubTitle(
                            eyeExamsConfig?.upcoming?.eyeExams,
                            { label: 'MANAGE_EYE_EXAMS' },
                            { label: 'LABEL' }
                        )}
                        subtitleClass={!userEyeExams ? 'disabled' : 'mb-10'}
                        ctaLabel={getText(
                            eyeExamsConfig?.upcoming?.eyeExams,
                            { label: 'MANAGE_EYE_EXAMS' },
                            { label: 'CTA_LABEL' }
                        )}
                        ctaOnClick={() =>
                            getCardCtaOnClick(eyeExamsConfig?.upcoming?.eyeExams, {
                                label: 'MANAGE_EYE_EXAMS',
                            })
                        }
                    >
                        {getCardContent(
                            eyeExamsConfig?.upcoming?.eyeExams?.[0],
                            { label: 'MANAGE_EYE_EXAMS' },
                            { label: 'TEXT' }
                        )}
                    </Card>
                );
            case 'STORES':
                return (
                    <Card
                        key={cardType}
                        icon={!myStores?.length ? <MyStores /> : null}
                        title={getText(myStores, { label: 'MY_STORE' }, { label: 'TITLE' })}
                        subtitle={getCardSubTitle(
                            myStores,
                            { label: 'MY_STORE' },
                            { label: 'LABEL' }
                        )}
                        subtitleClass={!myStores?.length ? 'disabled' : 'mb-10'}
                        ctaLabel={
                            myStores?.length
                                ? getText(myStores, { label: 'MY_STORE' }, { label: 'CTA_LABEL' })
                                : null
                        }
                        ctaOnClick={() => getCardCtaOnClick(myStores, { label: 'MY_STORE' })}
                    >
                        {getCardContent(myStores?.[0], { label: 'MY_STORE' }, { label: 'TEXT' })}
                    </Card>
                );
            /*
            case 'FYL':
                return (
                    <Card
                        key={cardType}
                        icon={<FindYourLook />}
                        title={getText(
                            findYourLook,
                            { label: 'FIND_YOUR_LOOK' },
                            { label: 'TITLE' }
                        )}
                        subtitle={getCardSubTitle(
                            findYourLook,
                            { label: 'FIND_YOUR_LOOK' },
                            { label: 'LABEL' }
                        )}
                        subtitleClass={!findYourLook ? 'disabled' : null}
                        ctaLabel={getText(
                            findYourLook,
                            { label: 'FIND_YOUR_LOOK' },
                            { label: 'CTA_LABEL' }
                        )}
                        ctaOnClick={() =>
                            getCardCtaOnClick(findYourLook, { label: 'FIND_YOUR_LOOK' })
                        }
                    >
                        {getCardContent(
                            findYourLook,
                            { label: 'FIND_YOUR_LOOK' },
                            { label: 'TEXT' }
                        )}
                    </Card>
                );
            */
            case 'WISHLIST':
                return (
                    <Card
                        key={cardType}
                        icon={!mySavedItems ? <MySavedItems /> : null}
                        title={getText(mySavedItems, { label: 'MY_FAVORITES' }, { label: 'TITLE' })}
                        subtitle={getCardSubTitle(
                            mySavedItems,
                            { label: 'MY_FAVORITES' },
                            { label: 'LABEL' }
                        )}
                        subtitleClass={!mySavedItems ? 'disabled' : 'mb-10'}
                        ctaLabel={getText(
                            mySavedItems,
                            { label: 'MY_FAVORITES' },
                            { label: 'CTA_LABEL' }
                        )}
                        ctaOnClick={() =>
                            getCardCtaOnClick(mySavedItems, { label: 'MY_FAVORITES' })
                        }
                    >
                        {getCardContent(mySavedItems, { label: 'MY_FAVORITES' }, { label: 'TEXT' })}
                    </Card>
                );
            case 'ORDERS':
                return (
                    <Card
                        key={cardType}
                        icon={!myOrders || !myOrders?.length ? <MyOrders /> : null}
                        iconClass={!myOrders || !myOrders?.length ? 'card-icon-my-orders' : ''}
                        title={getText(myOrders, { label: 'MY_ORDERS' }, { label: 'TITLE' })}
                        subtitle={(retailOrdersHistory.length > 0 || onlineOrdersHistory.length > 0)
                            ? retailOrdersHistory.length + onlineOrdersHistory.length + ' ORDERS'
                            : getCardSubTitle(
                                myOrders,
                                { label: 'MY_ORDERS' },
                                { label: 'LABEL' }
                            )
                        }
                        subtitleClass={!myOrders ? 'disabled' : 'mb-24'}
                        ctaLabel={getText(myOrders, { label: 'MY_ORDERS' }, { label: 'CTA_LABEL' })}
                        ctaOnClick={() => getCardCtaOnClick(myOrders, { label: 'MY_ORDERS' })}
                    >
                        {getCardContent(myOrders?.[0], { label: 'MY_ORDERS' }, { label: 'TEXT' })}
                    </Card>
                );
            case 'BTE':
                return inStoreAppointmentEspot && !inStoreAppointment?.appointmentsList ? (
                    <Card key={cardType}>
                        <div
                            onClick={goToFindStore}
                            className="card-nested"
                            dangerouslySetInnerHTML={{ __html: inStoreAppointmentEspot }}
                        />
                    </Card>
                ) : (
                    inStoreAppointment?.appointmentsList && (
                        <Card
                            key={cardType}
                            title={getText(
                                inStoreAppointment.appointmentsList,
                                { label: 'MY_INSTORE_APPOINTMENT' },
                                { label: 'TITLE' }
                            )}
                            subtitle={getCardSubTitle(
                                inStoreAppointment.appointmentsList,
                                { label: 'MY_INSTORE_APPOINTMENT' },
                                { label: 'LABEL' }
                            )}
                            subtitleClass={'mb-24'}
                            ctaLabel={getText(
                                inStoreAppointment.appointmentsList,
                                { label: 'MY_INSTORE_APPOINTMENT' },
                                { label: 'CTA_LABEL' }
                            )}
                            ctaOnClick={() =>
                                getCardCtaOnClick(inStoreAppointment.appointmentsList, {
                                    label: 'MY_INSTORE_APPOINTMENT',
                                })
                            }
                        >
                            {getCardContent(
                                inStoreAppointment,
                                { label: 'MY_INSTORE_APPOINTMENT' },
                                { label: 'TEXT' }
                            )}
                        </Card>
                    )
                );
            default:
                return <></>;
        }
    };

    return (
        <div>
            {!!lastContactLensesOrderId && !!dahsboardEspotReorderTop ? (
                <div>
                    <div
                        className="card-espot-top-reorder"
                        dangerouslySetInnerHTML={{ __html: dahsboardEspotReorderTop }}
                    />
                </div>
            ) : (
                !!dahsboardEspotTop && (
                    <div>
                        <div
                            className="card-espot-top"
                            dangerouslySetInnerHTML={{ __html: dahsboardEspotTop }}
                        />
                    </div>
                )
            )}
            <div className="card-container">
                {getDashboardInformationsRequestStatus === 'LOADING' &&
                    DASHBOARD_WIDGETS.map(tile => <LoaderElement key={tile} height={400} />)}
                {getDashboardInformationsRequestStatus === 'SUCCESS' &&
                    (dashboardInformations?.tiles.length
                        ? dashboardInformations.tiles.map(tile => getCard(tile))
                        : DASHBOARD_WIDGETS.map(tile => getCard(tile)))}
            </div>
            {!!dahsboardEspotBottom && (
                <div>
                    <div
                        className="card-espot-bottom"
                        dangerouslySetInnerHTML={{ __html: dahsboardEspotBottom }}
                    />
                </div>
            )}
        </div>
    );
};

export default CardContainer;
